<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue';
import EntityService from '@/services/entityService';
import Schema from '@/api/v2/Schema.mjs';
import UtilMoney from '@/utils/UtilMoney';
import UtilNumber from '@/utils/UtilNumber';
import { Unidade as UnidadeEnum } from '@/api/v2/enums/OrdemDeServicoItensEnum';

const props = defineProps({
    visible: { type: Boolean, default: false },
    item: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    podeEditarValorUnitario: { type: Boolean, default: false },
    podeEditarDataNecessidade: { type: Boolean, default: false },
    somenteStatusLCParaCamposSensiveis: { type: Boolean, default: false },
    dataNecessidadeSomenteLeitura: { type: Boolean, default: false },
});

const emits = defineEmits(['close', 'salvar']);
const salvando = ref(false);

const form = ref({});
const itensCatalogo = ref([]);
const unidades = ref([]);
const carregouListas = ref(false);

const upsertOpcao = (lista = [], opcao = {}, matchField = 'valueId') => {
    if (!opcao?.[matchField]) return lista;

    const idx = lista.findIndex((item = {}) => item?.[matchField] === opcao?.[matchField]);
    if (idx === -1) {
        return [...lista, opcao];
    }

    const novaLista = [...lista];
    novaLista[idx] = {
        ...novaLista[idx],
        ...opcao,
    };

    return novaLista;
};

const pegaId = (value = null) => {
    if (value && typeof value === 'object') return value.id || value._id || '';
    return value || '';
};

const primeiroValor = (...valores) => {
    for (const valor of valores) {
        if (valor !== undefined && valor !== null && valor !== '') return valor;
    }
    return '';
};

const dataAtualISO = () => new Date().toISOString().slice(0, 10);

const normalizaDataParaInput = (valor = '', fallback = '') => {
    const texto = String(valor || '').trim();
    if (!texto) return fallback;

    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
        return texto;
    }

    const matchISO = texto.match(/^(\d{4}-\d{2}-\d{2})/);
    if (matchISO?.[1]) {
        return matchISO[1];
    }

    const dataParseada = new Date(texto);
    if (!Number.isNaN(dataParseada.getTime())) {
        return dataParseada.toISOString().slice(0, 10);
    }

    return fallback;
};

const extraiListaResponse = (response = {}) => {
    if (Array.isArray(response?.data?.data?.data)) return response.data.data.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.data)) return response.data;
    return [];
};

const normalizaRegistro = (registro = {}) => {
    const currentData = registro?._currentData || registro?.currentData || {};
    const temDados = currentData && typeof currentData === 'object' && Object.keys(currentData).length > 0;
    const dados = temDados ? currentData : registro;

    return {
        ...dados,
        id: dados?.id || dados?._id || registro?.id || registro?._id || '',
        _id: dados?._id || registro?._id || dados?.id || registro?.id || '',
    };
};

const normalizaOpcaoItem = (item = {}) => {
    const normalizado = normalizaRegistro(item);
    const codigo = primeiroValor(normalizado?.codeExt, normalizado?.code, normalizado?.codigo, normalizado?.itemCodigo, '');
    const descricao = primeiroValor(normalizado?.description, normalizado?.descricao, normalizado?.name, normalizado?.nome, '');
    const valueId = primeiroValor(normalizado?.id, normalizado?._id, '');
    const display = [codigo, descricao].filter(Boolean).join(' - ') || descricao || codigo || 'Sem descrição';

    return {
        ...normalizado,
        valueId,
        display,
    };
};

const normalizaOpcaoUnidade = (unidade = {}) => {
    const normalizada = normalizaRegistro(unidade);
    const sigla = primeiroValor(normalizada?.sigla, normalizada?.code, normalizada?.codigo, '');
    const descricao = primeiroValor(normalizada?.descricao, normalizada?.description, normalizada?.name, normalizada?.nome, '');
    const valueId = primeiroValor(normalizada?.id, normalizada?._id, '');
    const display = [sigla, descricao].filter(Boolean).join(' - ') || descricao || sigla || 'Sem descrição';

    return {
        ...normalizada,
        valueId,
        display,
    };
};

const opcaoUnidadeTemLabel = (item = {}) => {
    const sigla = primeiroValor(item?.sigla, item?.code, item?.codigo, '');
    const descricao = primeiroValor(item?.descricao, item?.description, item?.name, item?.nome, '');
    return !!(String(sigla || '').trim() || String(descricao || '').trim());
};

const opcaoItemTemLabel = (item = {}) => {
    const codigo = primeiroValor(item?.codeExt, item?.code, item?.codigo, item?.itemCodigoExt, item?.itemCodigo, '');
    const descricao = primeiroValor(item?.description, item?.descricao, item?.name, item?.nome, '');
    return !!(String(codigo || '').trim() || String(descricao || '').trim());
};

const normalizaOpcaoItemDoOS = (itemOS = {}) => {
    const normalizado = normalizaRegistro(itemOS);
    const valueId = primeiroValor(pegaId(normalizado?.item), normalizado?.itemId, normalizado?.itemID, '');
    const codigo = primeiroValor(normalizado?.itemCodigoExt, normalizado?.itemCodigo, normalizado?.codigoItem, '');
    const descricao = primeiroValor(normalizado?.descricao, normalizado?.description, '');
    const display = [codigo, descricao].filter(Boolean).join(' - ') || descricao || codigo || 'Sem descrição';

    return {
        ...normalizado,
        id: valueId || normalizado?.id || normalizado?._id || '',
        _id: valueId || normalizado?._id || normalizado?.id || '',
        valueId,
        codeExt: codigo || normalizado?.codeExt || '',
        code: normalizado?.code || '',
        codigo: normalizado?.codigo || '',
        description: descricao || normalizado?.description || '',
        descricao: descricao || normalizado?.descricao || '',
        display,
    };
};

const carregaListas = async () => {
    if (carregouListas.value) return;

    try {
        const [itensResponse, unidadesResponse, itensDaOSResponse] = await Promise.all([
            new EntityService(Schema.ITEM).getAll(1, 20000, '', '', null, null, null, 'codeExt'),
            new EntityService(Schema.ORDEM_DE_SERVICO_ITEM_UNIDADE).getAll(1, 200, '', '', null, null, null, 'sigla'),
            new EntityService(Schema.ORDEM_DE_SERVICO_ITENS).getAll(1, 2000, '', '', null, null, null, 'itemCodigoExt'),
        ]);

        const itens = extraiListaResponse(itensResponse);
        const unidadesLista = extraiListaResponse(unidadesResponse);
        const itensDaOSLista = extraiListaResponse(itensDaOSResponse);

        let itensNormalizados = itens.map((item = {}) => normalizaOpcaoItem(item));
        let unidadesNormalizadas = unidadesLista.map((unidade = {}) => normalizaOpcaoUnidade(unidade));

        const itensSemLabel = itensNormalizados.filter((item = {}) => item?.valueId && !opcaoItemTemLabel(item));
        if (itensSemLabel.length > 0) {
            const itemService = new EntityService(Schema.ITEM);
            const itensResolvidos = await Promise.allSettled(
                itensSemLabel.map((item = {}) => itemService.getById(item.valueId))
            );

            const mapaItens = new Map(
                itensResolvidos
                    .filter((resultado) => resultado.status === 'fulfilled')
                    .map((resultado) => normalizaOpcaoItem(resultado?.value?.data?.data || resultado?.value?.data || {}))
                    .filter((item = {}) => !!item?.valueId)
                    .map((item = {}) => [item.valueId, item])
            );

            itensNormalizados = itensNormalizados.map((item = {}) => mapaItens.get(item.valueId) || item);
        }

        const mapaItens = new Map(
            itensNormalizados
                .filter((item = {}) => !!item?.valueId)
                .map((item = {}) => [item.valueId, item])
        );

        itensDaOSLista
            .map((item = {}) => normalizaOpcaoItemDoOS(item))
            .filter((item = {}) => !!item?.valueId)
            .forEach((item = {}) => {
                const atual = mapaItens.get(item.valueId);
                if (!atual || !opcaoItemTemLabel(atual)) {
                    mapaItens.set(item.valueId, item);
                }
            });

        itensNormalizados = Array.from(mapaItens.values());

        const unidadesSemLabel = unidadesNormalizadas.filter((item = {}) => item?.valueId && !opcaoUnidadeTemLabel(item));
        if (unidadesSemLabel.length > 0) {
            const unidadeService = new EntityService(Schema.ORDEM_DE_SERVICO_ITEM_UNIDADE);
            const unidadesResolvidas = await Promise.allSettled(
                unidadesSemLabel.map((item = {}) => unidadeService.getById(item.valueId))
            );

            const mapaUnidades = new Map(
                unidadesResolvidas
                    .filter((resultado) => resultado.status === 'fulfilled')
                    .map((resultado) => normalizaOpcaoUnidade(resultado?.value?.data?.data || resultado?.value?.data || {}))
                    .filter((item = {}) => !!item?.valueId)
                    .map((item = {}) => [item.valueId, item])
            );

            unidadesNormalizadas = unidadesNormalizadas.map((item = {}) => mapaUnidades.get(item.valueId) || item);
        }

        Object.values(UnidadeEnum || {}).forEach((unidade = {}) => {
            const opcao = normalizaOpcaoUnidade(unidade);
            if (!opcao?.valueId) return;

            const idx = unidadesNormalizadas.findIndex((item = {}) => item.valueId === opcao.valueId);
            if (idx === -1) {
                unidadesNormalizadas.push(opcao);
                return;
            }

            if (!opcaoUnidadeTemLabel(unidadesNormalizadas[idx])) {
                unidadesNormalizadas[idx] = opcao;
            }
        });

        const itensComLabel = itensNormalizados.filter((item = {}) => opcaoItemTemLabel(item));
        const unidadesComLabel = unidadesNormalizadas.filter((item = {}) => opcaoUnidadeTemLabel(item));

        itensCatalogo.value = itensComLabel.length > 0 ? itensComLabel : itensNormalizados;
        unidades.value = unidadesComLabel.length > 0 ? unidadesComLabel : unidadesNormalizadas;

        carregouListas.value = true;
    } catch (e) {
        console.log(e);
    }
};

const garanteSelecoesAtuaisNoCatalogo = async () => {
    const itemId = String(form.value?.item || '').trim();
    const unidadeId = String(form.value?.unidade || '').trim();

    if (itemId) {
        let opcaoAtual = itensCatalogo.value.find((item = {}) => item.valueId === itemId);

        if (!opcaoAtual || !opcaoItemTemLabel(opcaoAtual)) {
            try {
                const itemResponse = await new EntityService(Schema.ITEM).getById(itemId);
                const itemDetalhe = normalizaOpcaoItem(itemResponse?.data?.data || itemResponse?.data || {});
                const codigoFallback = primeiroValor(
                    form.value?.itemCodigoExt,
                    form.value?.itemCodigo,
                    props.item?.itemCodigoExt,
                    props.item?.itemCodigo,
                    props.item?.codigoItem,
                    ''
                );
                const descricaoFallback = primeiroValor(
                    form.value?.descricao,
                    props.item?.descricao,
                    props.item?.description,
                    ''
                );

                opcaoAtual = {
                    ...itemDetalhe,
                    valueId: itemDetalhe?.valueId || itemId,
                    codeExt: primeiroValor(itemDetalhe?.codeExt, itemDetalhe?.code, codigoFallback, ''),
                    description: primeiroValor(itemDetalhe?.description, itemDetalhe?.descricao, descricaoFallback, ''),
                };
                opcaoAtual.display = [opcaoAtual.codeExt, opcaoAtual.description].filter(Boolean).join(' - ') || opcaoAtual.description || opcaoAtual.codeExt || 'Sem descrição';
            } catch (e) {
                const codigoFallback = primeiroValor(form.value?.itemCodigoExt, form.value?.itemCodigo, props.item?.itemCodigoExt, props.item?.itemCodigo, props.item?.codigoItem, '');
                const descricaoFallback = primeiroValor(form.value?.descricao, props.item?.descricao, props.item?.description, '');

                opcaoAtual = {
                    valueId: itemId,
                    id: itemId,
                    _id: itemId,
                    codeExt: codigoFallback,
                    description: descricaoFallback,
                    display: [codigoFallback, descricaoFallback].filter(Boolean).join(' - ') || descricaoFallback || codigoFallback || 'Sem descrição',
                };
            }

            itensCatalogo.value = upsertOpcao(itensCatalogo.value, opcaoAtual, 'valueId');
        }
    }

    if (unidadeId) {
        let opcaoAtual = unidades.value.find((item = {}) => item.valueId === unidadeId);

        if (!opcaoAtual || !opcaoUnidadeTemLabel(opcaoAtual)) {
            try {
                const unidadeResponse = await new EntityService(Schema.ORDEM_DE_SERVICO_ITEM_UNIDADE).getById(unidadeId);
                const unidadeDetalhe = normalizaOpcaoUnidade(unidadeResponse?.data?.data || unidadeResponse?.data || {});
                const siglaFallback = primeiroValor(form.value?.unidadeSigla, props.item?.unidadeSigla, props.item?.siglaUnidade, '');

                opcaoAtual = {
                    ...unidadeDetalhe,
                    valueId: unidadeDetalhe?.valueId || unidadeId,
                    sigla: primeiroValor(unidadeDetalhe?.sigla, unidadeDetalhe?.code, siglaFallback, ''),
                    descricao: primeiroValor(unidadeDetalhe?.descricao, unidadeDetalhe?.description, ''),
                };
                opcaoAtual.display = [opcaoAtual.sigla, opcaoAtual.descricao].filter(Boolean).join(' - ') || opcaoAtual.descricao || opcaoAtual.sigla || 'Sem descrição';
            } catch (e) {
                const siglaFallback = primeiroValor(form.value?.unidadeSigla, props.item?.unidadeSigla, props.item?.siglaUnidade, '');

                opcaoAtual = {
                    valueId: unidadeId,
                    id: unidadeId,
                    _id: unidadeId,
                    sigla: siglaFallback,
                    descricao: '',
                    display: siglaFallback || 'Sem descrição',
                };
            }

            unidades.value = upsertOpcao(unidades.value, opcaoAtual, 'valueId');
        }
    }
};

const preparaFormulario = (item = {}) => {
    const itemRef = item?.item && typeof item.item === 'object' ? item.item : {};
    const unidadeRef = item?.unidade && typeof item.unidade === 'object' ? item.unidade : {};
    const unidadeCompraRef = item?.unidadeCompra && typeof item.unidadeCompra === 'object' ? item.unidadeCompra : {};

    const quantidade = UtilNumber.toNumber(primeiroValor(item?.quantidade, item?.qtd, item?.quantidadeSolicitada, 0));
    const valorUnitario = UtilNumber.toNumber(primeiroValor(item?.valorUnitario, item?.valorUnit, item?.precoUnitario, 0));
    const valorTotal = UtilNumber.toNumber(primeiroValor(item?.valorTotal, item?.precoTotal, quantidade * valorUnitario, 0));

    const dataSolicitacao = normalizaDataParaInput(primeiroValor(item?.dataSolicitacao, item?.createdAt, ''), dataAtualISO());

    form.value = {
        id: primeiroValor(item?.id, item?._id, ''),
        sequencia: String(primeiroValor(item?.sequencia, item?.sequence, '') || ''),
        item: primeiroValor(pegaId(item?.item), item?.itemId, item?.item || ''),
        itemCodigoExt: String(primeiroValor(item?.itemCodigoExt, item?.itemCodigo, item?.codigoItem, itemRef?.codeExt, itemRef?.code, itemRef?.codigo, '')),
        itemCodigo: String(primeiroValor(item?.itemCodigo, item?.codigoItem, itemRef?.code, itemRef?.codigo, '')),
        descricao: String(primeiroValor(item?.descricao, item?.description, itemRef?.description, itemRef?.descricao, '')),
        destino: primeiroValor(item?.destino, item?.destination, 'ND'),
        status: primeiroValor(item?.status, item?.situacao, 'ED'),
        unidade: primeiroValor(pegaId(item?.unidade), item?.unidadeId, item?.unidade || ''),
        unidadeSigla: String(primeiroValor(item?.unidadeSigla, item?.siglaUnidade, unidadeRef?.sigla, unidadeRef?.code, '')),
        unidadeCompra: primeiroValor(pegaId(item?.unidadeCompra), item?.unidadeCompraId, item?.unidadeCompra || ''),
        unidadeCompraSigla: String(primeiroValor(item?.unidadeCompraSigla, item?.siglaUnidadeCompra, unidadeCompraRef?.sigla, unidadeCompraRef?.code, '')),
        quantidade,
        quantidadeCompra: UtilNumber.toNumber(primeiroValor(item?.quantidadeCompra, item?.qtdComprada, item?.quantidadeComprada, 0)),
        quantidadeParaEntregar: 0,
        quantidadeEntregue: item?.quantidadeEntregue ?? 0,
        quantidadeParaEmbarcar: quantidade,
        quantidadeEmbarcada: item?.quantidadeEmbarcada ?? 0,
        pesoTotal: item?.pesoTotal ?? 0,
        valorUnitario,
        valorTotal,
        dataSolicitacao,
        dataNecessidade: normalizaDataParaInput(item?.dataNecessidade || '', dataSolicitacao),
        dataEntrega: normalizaDataParaInput(item?.dataEntrega || ''),
        desenhoFabricacao: item?.desenhoFabricacao || '',
        desenhoMontagem: item?.desenhoMontagem || '',
        fornecedor: item?.fornecedor || '',
    };
};

watch(() => props.item, (novo) => {
    preparaFormulario(novo || {});
}, { immediate: true });

watch(() => props.visible, (visivel) => {
    if (!visivel) return;

    const abrirModal = async () => {
        preparaFormulario(props.item || {});
        await carregaListas();
        await garanteSelecoesAtuaisNoCatalogo();
    };

    abrirModal();
});

watch(
    () => [form.value.quantidade, form.value.valorUnitario],
    () => {
        const quantidade = UtilNumber.toNumber(form.value.quantidade);
        const valorUnitario = UtilNumber.toNumber(form.value.valorUnitario);
        form.value.valorTotal = UtilNumber.round(quantidade * valorUnitario);
        if (!form.value.id) {
            form.value.quantidadeParaEmbarcar = quantidade;
        }
    },
    { deep: true }
);

const valorUnitarioDisplay = computed(() => UtilMoney.toBRL(form.value.valorUnitario || 0));
const valorTotalDisplay = computed(() => UtilMoney.toBRL(form.value.valorTotal || 0));
const statusNormalizado = computed(() => String(form.value?.status || '').trim().toUpperCase());
const camposSensiveisNoStatusPermitido = computed(() => (
    !props.somenteStatusLCParaCamposSensiveis || statusNormalizado.value === 'LC'
));
const bloqueiaCamposNaoSensiveis = computed(() => props.somenteStatusLCParaCamposSensiveis);
const podeEditarValorUnitarioNoFormulario = computed(() => (
    !props.disabled
    && props.podeEditarValorUnitario
    && camposSensiveisNoStatusPermitido.value
));
const podeEditarDataNecessidadeNoFormulario = computed(() => (
    !props.disabled
    && !props.dataNecessidadeSomenteLeitura
    && props.podeEditarDataNecessidade
    && camposSensiveisNoStatusPermitido.value
));
const podeSalvarItem = computed(() => (
    !props.disabled
    && (!props.somenteStatusLCParaCamposSensiveis || statusNormalizado.value === 'LC')
));

const aoAlterarItem = (itemId) => {
    const itemSelecionado = itensCatalogo.value.find((item) => item.valueId === itemId || item.id === itemId || item._id === itemId);
    if (!itemSelecionado) return;

    form.value.item = itemSelecionado.valueId || itemSelecionado.id || itemSelecionado._id || '';
    form.value.itemCodigo = itemSelecionado.code || itemSelecionado.codigo || '';
    form.value.itemCodigoExt = itemSelecionado.codeExt || itemSelecionado.code || itemSelecionado.codigo || '';
    if (!form.value.descricao) {
        form.value.descricao = itemSelecionado.description || itemSelecionado.descricao || '';
    }
};

const aoAlterarUnidade = (unidadeId) => {
    const unidadeSelecionada = unidades.value.find((unidade) => unidade.valueId === unidadeId || unidade.id === unidadeId || unidade._id === unidadeId);
    if (!unidadeSelecionada) return;

    form.value.unidade = unidadeSelecionada.valueId || unidadeSelecionada.id || unidadeSelecionada._id || '';
    form.value.unidadeSigla = unidadeSelecionada.sigla || '';
};

const aoAlterarValorUnitario = (valor) => {
    form.value.valorUnitario = UtilNumber.toNumber(valor);
};

const fechar = () => {
    emits('close');
};

const salvar = async () => {
    if (salvando.value) return;
    salvando.value = true;
    try {
        await emits('salvar', {
            ...form.value,
            item: form.value?.item || '',
            unidade: form.value?.unidade || '',
        });
        // O parent já fecha o modal, mas se não, garantimos aqui também:
        emits('close');
    } finally {
        salvando.value = false;
    }
};
</script>

<template>
    <v-dialog
        :value="visible"
        persistent
        max-width="1100px"
        @input="(valor) => { if (!valor) fechar(); }"
    >
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ form.id ? 'Editar Item da OS' : 'Adicionar Item da OS' }}</span>
            </v-card-title>

            <v-card-text>
                <v-row>
                    <v-col cols="12" md="2">
                        <v-text-field v-model="form.sequencia" label="Sequência" outlined hide-details :disabled="disabled || bloqueiaCamposNaoSensiveis" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-autocomplete
                            v-model="form.item"
                            :items="itensCatalogo"
                            item-text="display"
                            item-value="valueId"
                            label="Item"
                            outlined
                            hide-details
                            :disabled="disabled || bloqueiaCamposNaoSensiveis"
                            @change="aoAlterarItem"
                        />
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-text-field v-model="form.status" label="Status" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-text-field v-model="form.destino" label="Destino" outlined hide-details :disabled="disabled" v-show="false" />
                    </v-col>

                    <v-col cols="12" md="5">
                        <v-text-field v-model="form.descricao" label="Descrição" outlined hide-details :disabled="disabled || bloqueiaCamposNaoSensiveis" />
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-autocomplete
                            v-model="form.unidade"
                            :items="unidades"
                            item-text="display"
                            item-value="valueId"
                            label="Unidade"
                            outlined
                            hide-details
                            :disabled="disabled || bloqueiaCamposNaoSensiveis"
                            @change="aoAlterarUnidade"
                        />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model.number="form.quantidade" type="number" label="Quantidade" outlined hide-details :disabled="disabled || bloqueiaCamposNaoSensiveis" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model.number="form.quantidadeCompra" type="number" label="Qtd Comprada" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model.number="form.quantidadeParaEntregar" type="number" label="Qtd p/ Entregar" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model.number="form.quantidadeEntregue" type="number" label="Qtd Entregue" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model.number="form.quantidadeParaEmbarcar" type="number" label="Qtd p/ Embarcar" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model.number="form.quantidadeEmbarcada" type="number" label="Qtd Embarcada" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model.number="form.pesoTotal" type="number" label="Peso Total" outlined hide-details :disabled="disabled || bloqueiaCamposNaoSensiveis" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field
                            :value="valorUnitarioDisplay"
                            label="Valor Unitário"
                            outlined
                            hide-details
                            :disabled="!podeEditarValorUnitarioNoFormulario"
                            @input="aoAlterarValorUnitario"
                        />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field :value="valorTotalDisplay" label="Valor Total" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model="form.dataSolicitacao" type="date" label="Data Solicitação" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model="form.dataNecessidade" type="date" label="Data Necessidade" outlined hide-details :disabled="!podeEditarDataNecessidadeNoFormulario" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model="form.dataEntrega" type="date" label="Data Entrega" outlined hide-details readonly :disabled="true" />
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field v-model="form.desenhoFabricacao" label="Desenho Fabricação" outlined hide-details :disabled="disabled || bloqueiaCamposNaoSensiveis" />
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field v-model="form.desenhoMontagem" label="Desenho Montagem" outlined hide-details :disabled="disabled || bloqueiaCamposNaoSensiveis" />
                    </v-col>

                </v-row>
            </v-card-text>

            <v-card-actions class="justify-end">
                <v-btn text @click="fechar">Cancelar</v-btn>
                <v-btn color="primary" :disabled="!podeSalvarItem || salvando" @click="salvar">
                    <span v-if="!salvando">Salvar</span>
                    <span v-else>Salvando...</span>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped></style>
